// src/Analytics.tsx
import { useEffect } from "react";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const isDevelopment = process.env.NODE_ENV === 'development';

export function Analytics() {
  useEffect(() => {
    // Log para depuración
    console.log("🔍 Analytics: Inicializando GA4...");
    console.log("🔍 Analytics: GA_MEASUREMENT_ID =", GA_MEASUREMENT_ID);

    if (!GA_MEASUREMENT_ID) {
      console.warn("⚠️ Google Analytics ID no definido. Verifica tu archivo .env");
      return;
    }

    // Inicializar dataLayer ANTES de cargar el script (requerido por Google)
    const win = window as any;
    win.dataLayer = win.dataLayer || [];
    
    // Definir gtag temporal antes de que se cargue el script oficial
    // Esto asegura que las llamadas a gtag se almacenen en dataLayer
    // El script oficial de Google reemplazará esta función cuando se cargue
    win.gtag = win.gtag || function(...args: any[]) {
      win.dataLayer.push(args);
    };

    // Verificar si ya existe el script de GA
    const existingScript = document.querySelector(
      `script[src*="www.googletagmanager.com/gtag/js"]`
    );

    if (!existingScript) {
      console.log("📥 Analytics: Cargando script de GA4...");
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      // Esperar a que el script se cargue completamente
      script.onload = () => {
        console.log("✅ Analytics: Script de GA4 cargado correctamente");
        
        // Verificar que el script oficial haya reemplazado nuestra función gtag
        // El script oficial de Google debería haber reemplazado win.gtag
        const isOfficialGtag = win.gtag.toString().includes('dataLayer') && 
                               !win.gtag.toString().includes('function(...args: any[])');
        
        console.log("🔍 Analytics: Verificando gtag oficial...");
        console.log("   - Tipo:", typeof win.gtag);
        console.log("   - Es función oficial:", isOfficialGtag || win.gtag !== win.dataLayer.push);
        
        // Esperar un momento para que el script oficial se inicialice completamente
        setTimeout(() => {
          initializeGtag();
        }, 200);
      };
      
      script.onerror = () => {
        console.error("❌ Analytics: Error al cargar el script de GA4");
      };
      
      document.head.appendChild(script);
    } else {
      console.log("ℹ️ Analytics: Script de GA4 ya existe, inicializando gtag...");
      // Dar un delay para asegurar que el script oficial esté listo
      setTimeout(() => {
        initializeGtag();
      }, 200);
    }

    function initializeGtag() {
      const win = window as any;
      
      // Verificar que gtag esté disponible
      if (!win.gtag || typeof win.gtag !== 'function') {
        console.error("❌ Analytics: gtag no está disponible o no es una función");
        console.error("   - gtag:", win.gtag);
        return;
      }

      // Verificar que el script oficial esté cargado
      // El script oficial de Google Analytics debería estar disponible ahora
      const scriptLoaded = document.querySelector('script[src*="www.googletagmanager.com/gtag/js"]');
      if (!scriptLoaded) {
        console.error("❌ Analytics: Script de GA4 no encontrado en el DOM");
        return;
      }

      console.log("🔧 Analytics: Configurando GA4...");
      
      // Configurar GA4
      try {
        win.gtag("js", new Date());
        
        // Configurar GA4 con las opciones correctas
        const config: any = { 
          send_page_view: false // Desactivamos el page_view automático para manejarlo manualmente
        };
        
        // Solo activar debug_mode en desarrollo
        if (isDevelopment) {
          config.debug_mode = true;
        }
        
        win.gtag("config", GA_MEASUREMENT_ID, config);
        
        console.log("✅ Analytics: GA4 configurado correctamente");
        console.log("📊 Analytics: dataLayer length =", win.dataLayer?.length || 0);
        console.log("📊 Analytics: gtag disponible =", typeof win.gtag === 'function');
        
        // Verificar que los eventos se estén procesando
        // Esperar un momento y verificar si hay peticiones de red a Google Analytics
        setTimeout(() => {
          console.log("🔍 Analytics: Verificando procesamiento de eventos...");
          console.log("📊 Analytics: dataLayer después de config:", win.dataLayer?.length || 0);
          
          // Verificar si hay peticiones a Google Analytics en la red
          // Esto se puede verificar manualmente en la pestaña Network
          console.log("💡 Tip: Abre la pestaña Network y filtra por 'google-analytics' o 'collect'");
          console.log("   Deberías ver peticiones a www.google-analytics.com/g/collect");
        }, 1000);
        
      } catch (error) {
        console.error("❌ Analytics: Error al configurar GA4:", error);
      }
    }
  }, []);

  // No renderiza nada en pantalla
  return null;
}

